/**
 * Presentation formatting.
 *
 * Everything here is display-only. No function in this module derives an
 * engineering value: each one takes a value the backend already decided and
 * renders it. Display precision comes from the contract rather than from a
 * local guess, so the interface and the report round identically.
 */

import type { Area, Length, Quantity } from '../gen/ejot/roofing/v1/types';

/** Formats a contract quantity using the precision the backend specified. */
export function formatQuantity(q: Quantity | undefined, fallback = '-'): string {
  if (!q || q.value === undefined) return fallback;
  return formatNumber(q.value, q.display_precision ?? 2);
}

/** Formats a quantity followed by its unit, when it has one. */
export function formatQuantityWithUnit(q: Quantity | undefined, fallback = '-'): string {
  if (!q || q.value === undefined) return fallback;
  const value = formatNumber(q.value, q.display_precision ?? 2);
  return q.unit ? `${value} ${formatDisplayUnit(q.unit)}` : value;
}

/** Formats a length in millimetres. */
export function formatLength(l: Length | undefined, fallback = '-'): string {
  if (!l || l.millimetres === undefined) return fallback;
  return `${formatNumber(l.millimetres, 0)} mm`;
}

/** Formats a length in metres, for material-quantity contexts (rolls, takeoff). */
export function formatLengthMetres(l: Length | undefined, fallback = '-', precision = 2): string {
  if (!l || l.millimetres === undefined) return fallback;
  return `${formatNumber(l.millimetres / 1000, precision)} m`;
}

/** Formats an area in square metres. */
export function formatArea(a: Area | undefined, fallback = '-', precision = 1): string {
  if (!a || a.square_metres === undefined) return fallback;
  return `${formatNumber(a.square_metres, precision)} m²`;
}

/**
 * Formats a utilization fraction as a percentage.
 *
 * The fraction is the backend's verdict. Converting it to a percentage is a
 * change of presentation, not of meaning, and the pass or fail decision is
 * never re-derived from the number here.
 */
export function formatUtilization(q: Quantity | undefined, fallback = '-'): string {
  if (!q || q.value === undefined) return fallback;
  return `${formatNumber(q.value * 100, 0)}%`;
}

/** Formats a whole count with thousands separators. */
export function formatCount(n: number | undefined, fallback = '-'): string {
  if (n === undefined) return fallback;
  return formatNumber(n, 0);
}

function formatNumber(value: number, precision: number): string {
  return value.toLocaleString('en-GB', {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  });
}

/**
 * Converts plain contract-unit exponents to professional display text.
 *
 * Contracts deliberately keep machine-stable ASCII units such as `kN/m2` and
 * `kg/m3`. Presentation converts only the exponent glyph; the unit identity
 * and numeric value are unchanged. This also works in SVG/canvas labels where
 * semantic HTML superscript elements are unavailable.
 */
export function formatDisplayUnit(unit: string | undefined): string {
  if (!unit) return '';
  return unit
    .replace(/\^?2\b/g, '²')
    .replace(/\^?3\b/g, '³');
}

export interface TechnicalSymbolParts {
  base: string;
  sub?: string;
  sup?: string;
  suffix?: string;
}

/**
 * Parses the compact symbol notation used by the engineering contracts.
 *
 * Supported examples:
 * - `v_b,0` -> v with b,0 as subscript
 * - `q_p(z)` -> q with p as subscript followed by (z)
 * - `m^2` -> m with 2 as superscript
 *
 * This is typographic parsing only; it does not interpret or calculate the
 * engineering meaning of a symbol.
 */
export function parseTechnicalSymbol(symbol: string | undefined): TechnicalSymbolParts {
  if (!symbol) return { base: '' };

  const argumentIndex = symbol.indexOf('(');
  const core = argumentIndex >= 0 ? symbol.slice(0, argumentIndex) : symbol;
  const suffix = argumentIndex >= 0 ? symbol.slice(argumentIndex) : undefined;
  const underscoreIndex = core.indexOf('_');
  const caretIndex = core.indexOf('^');

  const baseEndCandidates = [underscoreIndex, caretIndex].filter((index) => index >= 0);
  const baseEnd = baseEndCandidates.length > 0 ? Math.min(...baseEndCandidates) : core.length;
  const base = displaySymbolBase(core.slice(0, baseEnd));

  let sub: string | undefined;
  let sup: string | undefined;
  if (underscoreIndex >= 0) {
    const end = caretIndex > underscoreIndex ? caretIndex : core.length;
    sub = core.slice(underscoreIndex + 1, end) || undefined;
  }
  if (caretIndex >= 0) {
    const end = underscoreIndex > caretIndex ? underscoreIndex : core.length;
    sup = core.slice(caretIndex + 1, end) || undefined;
  }

  return { base, sub, sup, suffix };
}

/**
 * Backward-compatible base/subscript split used by older components.
 * New rendering should prefer parseTechnicalSymbol so suffixes and superscripts
 * remain distinct.
 */
export function splitSymbol(symbol: string | undefined): { base: string; sub?: string } {
  const { base, sub } = parseTechnicalSymbol(symbol);
  return { base, sub };
}

function displaySymbolBase(base: string): string {
  const symbols: Record<string, string> = {
    alpha: 'α',
    beta: 'β',
    gamma: 'γ',
    rho: 'ρ',
  };
  return symbols[base] ?? base;
}
